import { lazy, Suspense, useEffect, useState } from 'react'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'
import { ErrorBoundary } from 'react-error-boundary'
import Header from './components/Header'
import Sidebar from './components/Sidebar'
import { ErrorFallback } from './components/ErrorFallback'
import { useEscape } from './lib/useEscape'
import { setAuthToken, subscribeToAuthFailure } from '@/lib/api'
import AdminLoginPage from './pages/AdminLoginPage'

const EventsPage = lazy(() => import('./pages/EventsPage'))
const CreateEventPage = lazy(() => import('./pages/CreateEventPage'))
const EventApplicationsPage = lazy(() => import('./pages/EventApplicationsPage'))
const EventPreviewPage = lazy(() => import('./pages/EventPreviewPage'))
const EventCheckInPage = lazy(() => import('./pages/EventCheckInPage'))
const EventSurveyPage = lazy(() => import('./pages/EventSurveyPage'))
const EventAutomationsPage = lazy(() => import('./pages/EventAutomationsPage'))
const EventResearchPage = lazy(() => import('./pages/EventResearchPage'))
const EventWaitlistPage = lazy(() => import('./pages/EventWaitlistPage'))
const EventInterviewsPage = lazy(() => import('./pages/EventInterviewsPage'))
const UsersPage = lazy(() => import('./pages/UsersPage'))
const UserProfileView = lazy(() => import('./components/user/UserProfileView'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const CreatorApplicationsPage = lazy(() => import('./pages/CreatorApplicationsPage'))
const CommunityPostsPage = lazy(() => import('./pages/CommunityPostsPage'))
const AccessControlPage = lazy(() => import('./pages/PostingAccessPage'))
const CommunitiesPage = lazy(() => import('./pages/CommunitiesPage'))
const ChannelsPage = lazy(() => import('./pages/ChannelsPage'))
const PlaybookSignupsPage = lazy(() => import('./pages/PlaybookSignupsPage'))
const WaitlistSignupsPage = lazy(() => import('./pages/WaitlistSignupsPage'))
const ClassroomPage = lazy(() => import('./pages/ClassroomPage'))
const WatchPage = lazy(() => import('./pages/WatchPage'))
const WatchAnalyticsPage = lazy(() => import('./pages/WatchAnalyticsPage'))
const CourseEditorPage = lazy(() => import('./pages/CourseEditorPage'))
const RGAApplicationsPage = lazy(() => import('./pages/RGAApplicationsPage'))
const InfluencerApplicationsPage = lazy(() => import('./pages/InfluencerApplicationsPage'))
const MarketingBroadcastPage = lazy(() => import('./pages/MarketingBroadcastPage'))
const NewsletterSubscribersPage = lazy(() => import('./pages/NewsletterSubscribersPage'))

type AdminSidebarUser = { name: string; initials: string; role: string }

function PageFallback() {
  return (
    <div className="flex items-center justify-center h-40 text-text-fainter text-sm">
      Loading…
    </div>
  )
}

function Layout({ user, onLogout }: { user?: AdminSidebarUser; onLogout: () => void }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const closeSidebar = () => setSidebarOpen(false)
  const toggleSidebar = () => setSidebarOpen((o) => !o)

  useEscape(closeSidebar, sidebarOpen)

  return (
    <div className="flex flex-col w-full h-screen overflow-hidden">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:px-3 focus:py-2 focus:bg-primary focus:text-white focus:rounded-md focus:text-sm"
      >
        Skip to content
      </a>
      <div className="relative flex min-h-0 flex-1 overflow-hidden">
        <button
          type="button"
          aria-label="Close sidebar"
          tabIndex={sidebarOpen ? 0 : -1}
          onClick={closeSidebar}
          className={`fixed inset-x-0 bottom-0 top-0 z-20 bg-black/40 lg:hidden cursor-default
            transition-opacity duration-300
            ${sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}
          `}
        />

        <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} user={user ?? undefined} onLogout={onLogout} />

        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <Header onMenuClick={toggleSidebar} sidebarOpen={sidebarOpen} onLogout={onLogout} />
          <main id="main" aria-label="Main content" className="flex-1 overflow-auto bg-secondary">
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <Suspense fallback={<PageFallback />}>
                <Outlet />
              </Suspense>
            </ErrorBoundary>
          </main>
        </div>
      </div>
    </div>
  )
}

function ProtectedLayout({
  token,
  user,
  onLogout,
}: {
  token: string | null
  user: AdminSidebarUser | null
  onLogout: () => void
}) {
  if (!token) return <Navigate to="/login" replace />
  return <Layout user={user ?? undefined} onLogout={onLogout} />
}

export default function App() {
  const ADMIN_TOKEN_KEY = 'adminAuthToken'
  const [adminToken, setAdminToken] = useState<string | null>(() => localStorage.getItem(ADMIN_TOKEN_KEY))
  const [adminUser, setAdminUser] = useState<AdminSidebarUser | null>(null)

  useEffect(() => {
    setAuthToken(adminToken)
  }, [adminToken])

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY)
    setAdminToken(null)
    setAdminUser(null)
    setAuthToken(null)
  }

  useEffect(() => {
    return subscribeToAuthFailure(() => {
      handleLogout()
    })
  }, [])

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <AdminLoginPage
            onLogin={({ token, user }) => {
              localStorage.setItem(ADMIN_TOKEN_KEY, token)
              setAdminToken(token)
              setAdminUser(user)
              setAuthToken(token)
            }}
          />
        }
      />
      <Route element={<ProtectedLayout token={adminToken} user={adminUser} onLogout={handleLogout} />}>
        <Route index element={<DashboardPage />} />
        <Route path="events" element={<EventsPage />} />
        <Route path="events/new" element={<CreateEventPage />} />
        <Route path="events/:id/edit" element={<CreateEventPage />} />
        <Route path="events/preview/:slug" element={<EventPreviewPage />} />
        <Route path="events/:eventId/applications" element={<EventApplicationsPage />} />
        <Route path="events/:eventId/check-in" element={<EventCheckInPage />} />
        <Route path="events/:eventId/survey" element={<EventSurveyPage />} />
        <Route path="events/:eventId/automations" element={<EventAutomationsPage />} />
        <Route path="events/:eventId/research" element={<EventResearchPage />} />
        <Route path="events/:eventId/waitlist" element={<EventWaitlistPage />} />
        <Route path="events/:eventId/interviews" element={<EventInterviewsPage />} />
        <Route path="watch" element={<WatchPage />} />
        <Route path="watch/analytics" element={<WatchAnalyticsPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="users/:userId" element={<UserProfileView />} />
        <Route path="creator-applications" element={<CreatorApplicationsPage />} />
        <Route path="community/access" element={<AccessControlPage />} />
        <Route path="community/posts" element={<CommunityPostsPage />} />
        <Route path="community/communities" element={<CommunitiesPage />} />
        <Route path="community/channels" element={<ChannelsPage />} />
        <Route path="classroom" element={<ClassroomPage />} />
        <Route path="classroom/:communitySlug/:courseSlug" element={<CourseEditorPage />} />
        <Route path="rga-applications" element={<RGAApplicationsPage />} />
        <Route path="playbook-signups" element={<PlaybookSignupsPage />} />
        <Route path="waitlist-signups" element={<WaitlistSignupsPage />} />
        <Route path="influencer-applications" element={<InfluencerApplicationsPage />} />
        <Route path="marketing/broadcast" element={<MarketingBroadcastPage />} />
        <Route path="marketing/newsletter" element={<NewsletterSubscribersPage />} />
        <Route path="*" element={<DashboardPage />} />
      </Route>
    </Routes>
  )
}
